import { useState } from "react";

import { Button } from "@/common/ui/button";
import { Input } from "@/common/ui/input";

interface AddEmailFormProps {
  onAdd: (email: string) => Promise<{ success: boolean; message: string }>;
  onCancel: () => void;
}

export function AddEmailForm({ onAdd, onCancel }: AddEmailFormProps): React.ReactElement {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(
    null
  );

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setIsSubmitting(true);
    setFeedback(null);

    try {
      const result = await onAdd(trimmed);

      if (result.success) {
        setFeedback({ type: "success", message: result.message });
        setEmail("");
      } else {
        setFeedback({ type: "error", message: result.message });
      }
    } catch {
      setFeedback({ type: "error", message: "An unexpected error occurred." });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div data-testid="add-email-form" className="rounded-lg border bg-card p-4">
      <form onSubmit={(e) => void handleSubmit(e)} className="flex items-center gap-3">
        <Input
          data-testid="email-input"
          type="email"
          placeholder="user@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFeedback(null);
          }}
          disabled={isSubmitting}
          required
          className="max-w-sm"
        />
        <Button data-testid="submit-email" type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? "Adding..." : "Add"}
        </Button>
        <Button
          data-testid="cancel-add"
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </form>
      {feedback && (
        <p
          data-testid="add-feedback"
          className={`mt-2 text-sm ${feedback.type === "success" ? "text-green-600 dark:text-green-400" : "text-destructive"}`}
        >
          {feedback.message}
        </p>
      )}
    </div>
  );
}
